import React from 'react';
import { Input } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';

export interface PriceSummaryTableRowProps {
    label: React.ReactNode;
    indented?: boolean;

    faceValue?: number | null;
    onFaceValueChange?: (v: number | null) => void;
    faceValueAriaLabel: string;
    faceValueErrorKey?: string;
    allowNegativeFaceValue?: boolean;
    percent?: string | null;

    showEmployeeCols: boolean;
    considerationForEmployees?: number | null;
    onConsiderationChange?: (v: number | null) => void;
    considerationAriaLabel?: string;
    considerationErrorKey?: string;
    netToSeller?: number | null;
    netToSellerAriaLabel?: string;

    showFairValueCols: boolean;
    fairValue?: number | null;
    onFairValueChange?: (v: number | null) => void;
    fairValueAriaLabel?: string;
    fairValueErrorKey?: string;
    explanation?: string | null;
    onExplanationChange?: (v: string | null) => void;
    explanationAriaLabel?: string;

    showComments: boolean;
    commentNode?: React.ReactNode;

    readOnly?: boolean;
    errors?: Set<string>;
}

export function parseCurrencyValue(value: string | undefined): number | null {
    return value === undefined || value === '' || value === 'NaN' ? null : Number(value);
}

export const PriceSummaryTableRow: React.FC<PriceSummaryTableRowProps> = ({
    label,
    indented,
    faceValue,
    onFaceValueChange,
    faceValueAriaLabel,
    faceValueErrorKey,
    allowNegativeFaceValue,
    percent,
    showEmployeeCols,
    considerationForEmployees,
    onConsiderationChange,
    considerationAriaLabel,
    considerationErrorKey,
    netToSeller,
    netToSellerAriaLabel,
    showFairValueCols,
    fairValue,
    onFairValueChange,
    fairValueAriaLabel,
    fairValueErrorKey,
    explanation,
    onExplanationChange,
    explanationAriaLabel,
    showComments,
    commentNode,
    readOnly,
    errors,
}) => (
    <tr>
        <td className={`terms-form-col-item table-label-cell${indented ? ' terms-form-indented' : ''}`}>
            {label}
        </td>
        <td className="terms-form-col-value">
            <CurrencyInput
                value={faceValue != null ? String(faceValue) : ''}
                allowDecimals
                allowNegativeValue={allowNegativeFaceValue ?? false}
                prefix="$"
                disabled={readOnly || !onFaceValueChange}
                onValueChange={onFaceValueChange ? (v) => onFaceValueChange(parseCurrencyValue(v)) : undefined}
                className={`form-control loi-form-input${faceValueErrorKey && errors?.has(faceValueErrorKey) ? ' is-invalid' : ''}`}
                aria-label={faceValueAriaLabel}
            />
        </td>
        <td className={`terms-form-col-percent table-label-cell${percent ? ' terms-form-percent-label' : ''}`}>
            {percent ?? ''}
        </td>
        {showEmployeeCols && (
            <>
                <td className="terms-form-col-value">
                    {onConsiderationChange && (
                        <CurrencyInput
                            value={considerationForEmployees != null ? String(considerationForEmployees) : ''}
                            allowDecimals
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(v) => onConsiderationChange(parseCurrencyValue(v))}
                            className={`form-control loi-form-input${considerationErrorKey && errors?.has(considerationErrorKey) ? ' is-invalid' : ''}`}
                            aria-label={considerationAriaLabel ?? ''}
                        />
                    )}
                </td>
                <td className="terms-form-col-value">
                    {netToSeller !== undefined && (
                        <CurrencyInput
                            value={netToSeller != null ? String(netToSeller) : ''}
                            allowDecimals
                            allowNegativeValue
                            prefix="$"
                            disabled
                            className="form-control loi-form-input"
                            aria-label={netToSellerAriaLabel ?? ''}
                        />
                    )}
                </td>
            </>
        )}
        {showFairValueCols && (
            <>
                <td className="terms-form-col-value">
                    {onFairValueChange && (
                        <CurrencyInput
                            value={fairValue != null ? String(fairValue) : ''}
                            allowDecimals
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(v) => onFairValueChange(parseCurrencyValue(v))}
                            className={`form-control loi-form-input${fairValueErrorKey && errors?.has(fairValueErrorKey) ? ' is-invalid' : ''}`}
                            aria-label={fairValueAriaLabel ?? ''}
                        />
                    )}
                </td>
                <td className="terms-form-col-explanation">
                    {onExplanationChange && (
                        <Input
                            type="text"
                            value={explanation ?? ''}
                            disabled={readOnly}
                            onChange={(e) => onExplanationChange(e.target.value === '' ? null : e.target.value)}
                            className="form-control loi-form-input"
                            aria-label={explanationAriaLabel ?? ''}
                        />
                    )}
                </td>
            </>
        )}
        {showComments && (
            <td className="terms-form-col-comment">
                {commentNode}
            </td>
        )}
    </tr>
);
